import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Typography, Space, Spin, Row, Col } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, EyeOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useResponsive } from '../hooks/useResponsive';
import { useSecurityProtection } from '../hooks/useSecurityProtection';
import { useCourseDetails, useCourseLessons } from '../hooks/course';
import { useUserProgressWithCourseDetails } from '../hooks/user';
import { TopicProgress, LessonProgress, UserProgressWithCourseDetails } from '../types';

const { Title, Text } = Typography;

const CourseLearning: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  useSecurityProtection();
  
  // State must be declared at the top level
  const [currentTopicIndex, setCurrentTopicIndex] = useState(0);
  
  const { data: courseDetails, isLoading: courseLoading } = useCourseDetails(undefined, courseId);
  const { data: lessons, isLoading: lessonsLoading } = useCourseLessons(undefined, courseId);
  const { data: userProgress, isLoading: progressLoading, error: progressError } = useUserProgressWithCourseDetails(courseId);

  // Process user progress data - validate and extract topics
  const progressTopics: TopicProgress[] = userProgress || [];
  
  // Set current topic based on index
  const currentTopic = progressTopics.length > 0 ? progressTopics[currentTopicIndex] : null;
  
  // Reset topic index when progress data changes - useEffect must be at top level
  useEffect(() => {
    if (progressTopics.length > 0 && currentTopicIndex >= progressTopics.length) {
      setCurrentTopicIndex(0);
    }
  }, [progressTopics.length, currentTopicIndex]);

  // Debug the user progress data structure
  console.log('User Progress Data:', userProgress);
  console.log('User Progress Type:', typeof userProgress);
  console.log('Is Array:', Array.isArray(userProgress));
  
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleLessonClick = (lessonId: string, lessonTitle?: string) => {
    // Navigate to specific lesson with title as state
    navigate(`/course/${courseId}/lesson/${lessonId}`, {
      state: { lessonTitle: lessonTitle }
    });
  };

  if (courseLoading || lessonsLoading || progressLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Spin size="large" />
      </div>
    );
  }


  
  // Helper function to check if lesson is completed
  const isLessonCompleted = (lessonId: string | number) => {
    for (const topic of progressTopics) {
      const lesson = topic.lessons.find(l => l.lesson_id.toString() === lessonId.toString());
      if (lesson) {
        return lesson.is_completed === 1;
      }
    }
    return false;
  };

  // Helper function to get lesson progress status
  const getLessonStatus = (lessonId: string | number, index: number) => {
    const completed = isLessonCompleted(lessonId);
    const isFirst = index === 0;
    
    return {
      completed,
      isActive: isFirst && !completed,
      isLocked: !isFirst && !completed && !isLessonCompleted(lessons?.[index - 1]?.id)
    };
  };

  return (
    <div style={{ 
      maxWidth: '1400px',
      margin: '0 auto',
      padding: isMobile ? '16px' : '32px 24px',
      background: '#f8f9fa',
      minHeight: '100vh'
    }}>
      <Row gutter={[40, 40]}>
        {/* Left Column - Course Info */}
        <Col xs={24} lg={10}>
          <Card style={{ 
            borderRadius: '20px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            overflow: 'hidden',
            position: 'sticky',
            top: '0',
            zIndex: 30
          }}>
            {/* Course Image with Grid Pattern */}
            {/* <div style={{
              background: '#4096ff',
              padding: '40px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '4px',
                width: 'fit-content',
                padding: '20px',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '12px'
              }}>
                {[
                  [4, 9, 2, 14],
                  [12, 7, 15, 8],
                  [5, 13, 6, 11],
                  [1, 6, 3, 10]
                ].map((row, rowIndex) => 
                  row.map((num, colIndex) => (
                    <div 
                      key={`${rowIndex}-${colIndex}`}
                      style={{
                        width: '40px',
                        height: '40px',
                        backgroundColor: (rowIndex === 0 && colIndex === 1) || (rowIndex === 1 && colIndex === 2) ? 
                          '#faad14' : 'rgba(255,255,255,0.9)',
                        color: (rowIndex === 0 && colIndex === 1) || (rowIndex === 1 && colIndex === 2) ? 
                          'white' : '#4096ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontWeight: '600'
                      }}
                    >
                      {num}
                    </div>
                  ))
                )}
              </div>
            </div> */}
            <img 
              src={courseDetails?.thumbnail_url || '/images/default-course.png'} 
              alt={courseDetails?.title || 'Course Thumbnail'}
              style={{ 
                width: isMobile ? '40%' : '50%', 
                height: isMobile ? '60px' : '150px',
                objectFit: 'cover',
                borderRadius: isMobile ? '6px' : '10px',
              }} 
            />

            <div style={{ padding: isMobile ? '16px' : '24px' }}>
              <Title level={2} style={{ margin: '0 0 12px 0', fontSize: isMobile ? '18px' : '24px' }}>
                {courseDetails?.title || 'Number Theory'}
              </Title>
              
              <Text style={{ 
                color: '#666', 
                fontSize: isMobile ? '11px' : '14px',
                lineHeight: '1.5',
                display: 'block',
                marginBottom: isMobile ? '16px' : '20px'
              }}>
                {courseDetails?.description || 'Explore the powers of divisibility, modular arithmetic, and infinity.'}
              </Text>

              {/* Course Stats */}
              <div style={{ 
                display: 'flex', 
                gap: isMobile ? '16px' : '20px',
                borderTop: '1px solid #f0f0f0',
                paddingTop: isMobile ? '12px' : '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <EyeOutlined style={{ color: '#666', fontSize: isMobile ? '12px' : '14px' }} />
                  <Text style={{ color: '#666', fontSize : isMobile ? '10px' : '14px' }}>
                    {currentTopic?.lessons?.length || lessons?.length || 75} Lessons
                  </Text>
                </div>
                {/* <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <ThunderboltOutlined style={{ color: '#666', fontSize: isMobile ? '12px' : '14px' }} />
                  <Text style={{ color: '#666', fontSize : isMobile ? '10px' : '14px' }}>
                    {courseDetails?.exercisesCount || 300} Exercises
                  </Text>
                </div> */}
                {/* Progress Stats */}
                {currentTopic?.lessons && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{
                      width: isMobile ? '10px' : '12px',
                      height: isMobile ? '10px' : '12px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #2c479e 30%, #182867 100%)'
                    }} />
                    <Text style={{ color: '#666', fontSize : isMobile ? '10px' : '14px' }}>
                      {currentTopic.lessons.filter(l => l.is_completed === 1).length}/{currentTopic.lessons.length} Complete
                    </Text>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </Col>

        {/* Right Column - Learning Path */}
        <Col xs={24} lg={14}>
          {/* <div style={{ marginBottom: '24px' }}>
            <Button 
              icon={<ArrowLeftOutlined />}
              onClick={handleBackToDashboard}
              style={{ 
                border: 'none',
                background: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                borderRadius: '12px',
                padding: '8px 20px',
                height: 'auto'
              }}
              size="large"
            >
              Back
            </Button>
          </div> */}

          {/* Current Level Banner with Navigation */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '12px' : '16px',
            marginBottom: isMobile ? '16px' : '24px'
          }}>
            {/* Left Arrow Button */}
            <Button
              type="text"
              onClick={() => {
                if (currentTopicIndex > 0) {
                  setCurrentTopicIndex(currentTopicIndex - 1);
                  console.log('Switched to previous topic:', progressTopics[currentTopicIndex - 1]);
                }
              }}
              disabled={!currentTopic || currentTopicIndex === 0}
              style={{
                border: 'none',
                background: 'white',
                borderRadius: '50%',
                width: isMobile ? '36px' : '44px',
                height: isMobile ? '36px' : '44px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: (!currentTopic || currentTopicIndex === 0) ? 0.5 : 1
              }}
            >
              <ArrowLeftOutlined style={{ 
                fontSize: isMobile ? '16px' : '20px',
                color: '#8c8c8c'
              }} />
            </Button>

            {/* Current Level Banner */}
            <div style={{
              flex: 1,
              background: 'linear-gradient(135deg, #2c479e 30%, #182867 100%)',
              borderRadius: isMobile ? '10px' : '14px',
              padding: isMobile ? '10px 14px' : '16px 20px',
              color: 'white',
              textAlign: 'center'
            }}>
              {/* <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: isMobile ? '10px' : '12px', fontWeight: '500' }}>
                Topic {currentTopic?.topic_order_index !== undefined ? currentTopic.topic_order_index + 1 : 1}
              </Text> */}
              <Title level={3} style={{ color: 'white', margin: '2px 0 0 0', fontSize: isMobile ? '13px' : '16px' }}>
                {currentTopic?.topic_title || 'Economics'}
              </Title>
              {progressTopics.length > 1 && (
                <Text style={{ 
                  color: 'rgba(255,255,255,0.6)', 
                  fontSize: isMobile ? '8px' : '10px',
                  marginTop: '4px',
                  display: 'block'
                }}>
                  {currentTopicIndex + 1} of {progressTopics.length} topics
                </Text>
              )}
            </div>

            {/* Right Arrow Button */}
            <Button
              type="text"
              onClick={() => {
                if (currentTopicIndex < progressTopics.length - 1) {
                  setCurrentTopicIndex(currentTopicIndex + 1);
                  console.log('Switched to next topic:', progressTopics[currentTopicIndex + 1]);
                }
              }}
              disabled={!currentTopic || currentTopicIndex === progressTopics.length - 1}
              style={{
                border: 'none',
                background: 'white',
                borderRadius: '50%',
                width: isMobile ? '36px' : '44px',
                height: isMobile ? '36px' : '44px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: (!currentTopic || currentTopicIndex === progressTopics.length - 1) ? 0.5 : 1
              }}
            >
              <ArrowRightOutlined style={{ 
                fontSize: isMobile ? '16px' : '20px',
                color: '#8c8c8c'
              }} />
            </Button>
          </div>

          {/* Debug Information */}
          {/* {process.env.NODE_ENV === 'development' && userProgress && (
            <div style={{ 
              marginBottom: '24px', 
              padding: '16px', 
              backgroundColor: '#f0f8ff', 
              borderRadius: '8px',
              border: '1px solid #91d5ff'
            }}>
              <Text strong style={{ color: '#0958d9' }}>Debug - User Progress Data:</Text>
              <div style={{ marginTop: '8px', fontSize: '12px', fontFamily: 'monospace' }}>
                <div>Status: {userProgress.status}</div>
                <div>Topics count: {progressTopics.length}</div>
                <div>Current topic: {currentTopic?.topic_title}</div>
                <div>Total lessons in current topic: {currentTopic?.lessons?.length || 0}</div>
              </div>
            </div>
          )} */}

          {/* Learning Path */}
          <div style={{ position: 'relative' }}>
            {/* Connecting Line */}
            <div style={{
              position: 'absolute',
              left: '50%',
              top: '60px',
              bottom: '60px',
              width: '2px',
              background: 'linear-gradient(to bottom, #e6f7ff, #bae7ff)',
              transform: 'translateX(-50%)',
              zIndex: 1
            }} />

            <Space direction="vertical" size={isMobile ? 12 : 24} style={{ width: '100%' }}>
              {/* Render lessons from progress data if available, otherwise fallback to lessons */}
              {(currentTopic && currentTopic.lessons && currentTopic.lessons.length > 0 ? 
                currentTopic.lessons.map((progressLesson: LessonProgress, index: number) => {
                  const lessonStatus = {
                    completed: progressLesson.is_completed === 1,
                    isActive: index === 0,
                    isLocked: false
                  };

                  return (
                    <div key={progressLesson.lesson_id} style={{ position: 'relative', zIndex: 2 }}>
                      <Card
                        hoverable={!lessonStatus.isLocked}
                        onClick={() => !lessonStatus.isLocked && handleLessonClick(progressLesson.lesson_id.toString(), progressLesson.lesson_name)}
                        style={{
                          borderRadius: isMobile ? '10px' : '16px',
                          border: lessonStatus.completed ? '2px solid transparent' : 'none',
                          background: lessonStatus.completed ? 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #667eea 0%, #764ba2 100%) border-box' : 'white',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                          cursor: lessonStatus.isLocked ? 'not-allowed' : 'pointer',
                          overflow: 'hidden',
                          opacity: lessonStatus.isLocked ? 0.6 : 1
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: isMobile ? 'flex-start' : 'center',
                          padding: isMobile ? '12px 10px' : '24px 16px'
                        }}>
                          {/* Lesson Icon/Circle */}
                          <div style={{
                            width: isMobile ? '50px' : '80px',
                            height: isMobile ? '50px' : '80px',
                            borderRadius: '50%',
                            background: lessonStatus.completed ?
                              'linear-gradient(135deg, #2c479e 0%, #182867 100%)' :
                              lessonStatus.isActive ?
                              'linear-gradient(135deg, #2c479e 0%, #182867 100%)' :
                              '#f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: isMobile ? '10px' : '16px',
                            flexShrink: 0
                          }}>
                            <div style={{
                              width: isMobile ? '25px' : '40px',
                              height: isMobile ? '25px' : '40px',
                              borderRadius: '50%',
                              background: lessonStatus.completed || lessonStatus.isActive ? 'white' : '#f5f5f5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <div style={{
                                width: isMobile ? '12px' : '20px',
                                height: isMobile ? '12px' : '20px',
                                borderRadius: '50%',
                                background: lessonStatus.completed ? '#182867' : lessonStatus.isActive ? '#182867' : '#bfbfbf'
                              }} />
                            </div>
                          </div>
                          {/* Lesson Content */}
                          <div style={{ flex: 1, textAlign: 'left' }}>
                            <Title level={4} style={{ 
                              margin: '0 0 2px 0',
                              color: lessonStatus.isActive ? '#1890ff' : '#262626',
                              fontSize: isMobile ? '13px' : '15px'
                            }}>
                              {progressLesson.lesson_name}
                            </Title>
                            <Text style={{ 
                              color: lessonStatus.isActive ? '#666' : '#8c8c8c',
                              fontSize: isMobile ? '10px' : '13px'
                            }}>
                            </Text>
                          </div>
                        </div>
                      </Card>
                    </div>
                  );
                }) :
                lessons?.map((lesson: any, index: number) => {
                  const lessonStatus = getLessonStatus(lesson.id, index);
                  return (
                    <div key={lesson.id || index} style={{ position: 'relative', zIndex: 2 }}>
                      <Card
                        hoverable={!lessonStatus.isLocked}
                        onClick={() => !lessonStatus.isLocked && handleLessonClick(lesson.id, lesson.title)}
                        style={{
                          borderRadius: isMobile ? '10px' : '16px',
                          border: lessonStatus.completed ? '2px solid transparent' : 'none',
                          background: lessonStatus.completed ? 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #667eea 0%, #764ba2 100%) border-box' : 'white',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                          cursor: lessonStatus.isLocked ? 'not-allowed' : 'pointer',
                          overflow: 'hidden',
                          opacity: lessonStatus.isLocked ? 0.6 : 1
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: isMobile ? 'flex-start' : 'center',
                          padding: isMobile ? '12px 10px' : '24px 16px'
                        }}>
                          {/* Lesson Icon/Circle */}
                          <div style={{
                            width: isMobile ? '50px' : '80px',
                            height: isMobile ? '50px' : '80px',
                            borderRadius: '50%',
                            background: lessonStatus.completed ?
                              'linear-gradient(135deg, #2c479e 0%, #182867 100%)' :
                              lessonStatus.isActive ?
                              'linear-gradient(135deg, #2c479e 0%, #182867 100%)' :
                              '#f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: isMobile ? '10px' : '16px',
                            flexShrink: 0
                          }}>
                            <div style={{
                              width: isMobile ? '25px' : '40px',
                              height: isMobile ? '25px' : '40px',
                              borderRadius: '50%',
                              background: lessonStatus.completed || lessonStatus.isActive ? 'white' : '#f5f5f5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <div style={{
                                width: isMobile ? '12px' : '20px',
                                height: isMobile ? '12px' : '20px',
                                borderRadius: '50%',
                                background: lessonStatus.completed ? '#182867' : lessonStatus.isActive ? '#182867' : '#bfbfbf'
                              }} />
                            </div>
                          </div>
                          {/* Lesson Content */}
                          <div style={{ flex: 1, textAlign: 'left' }}>
                            <Title level={4} style={{ 
                              margin: '0 0 2px 0',
                              color: lessonStatus.isActive ? '#1890ff' : '#262626',
                              fontSize: isMobile ? '13px' : '15px'
                            }}>
                              {lesson.title || ['Last Digits', 'Secret Messages', 'Rainbow Cycles'][index]}
                            </Title>
                            <Text style={{ 
                              color: lessonStatus.isActive ? '#666' : '#8c8c8c',
                              fontSize: isMobile ? '10px' : '13px'
                            }}>
                              {lesson.description}
                            </Text>
                          </div>
                        </div>
                      </Card>
                    </div>
                  );
                })
              ) || 
              // Fallback lessons if no data
              ['Last Digits', 'Secret Messages', 'Rainbow Cycles'].map((title, index) => (
                <div key={index} style={{ position: 'relative', zIndex: 2 }}>
                  <Card
                    hoverable
                    onClick={() => handleLessonClick(`lesson-${index + 1}`, title)}
                    style={{
                      borderRadius: isMobile ? '10px' : '16px',
                      border: 'none',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      cursor: 'pointer',
                      overflow: 'hidden'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: isMobile ? 'flex-start' : 'center',
                      padding: isMobile ? '12px 10px' : '24px 16px'
                    }}>
                      <div style={{
                        width: isMobile ? '50px' : '80px',
                        height: isMobile ? '50px' : '80px',
                        borderRadius: '50%',
                        background: index === 0 ? 
                          'linear-gradient(135deg, #4096ff 0%, #1890ff 100%)' :
                          '#f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: isMobile ? '10px' : '16px',
                        flexShrink: 0
                      }}>
                        <div style={{
                          width: isMobile ? '25px' : '40px',
                          height: isMobile ? '25px' : '40px',
                          borderRadius: '50%',
                          background: index === 0 ? 'white' : '#f5f5f5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <div style={{
                            width: isMobile ? '12px' : '20px',
                            height: isMobile ? '12px' : '20px',
                            borderRadius: '50%',
                            background: index === 0 ? '#182867' : '#bfbfbf'
                          }} />
                        </div>
                      </div>

                      <div style={{ flex: 1, textAlign: 'left' }}>
                        <Title level={4} style={{ 
                          margin: '0 0 2px 0',
                          color: index === 0 ? '#1890ff' : '#8c8c8c',
                          fontSize: isMobile ? '13px' : '15px'
                        }}>
                          {title}
                        </Title>
                        <Text style={{ 
                          color: index === 0 ? '#666' : '#bfbfbf',
                          fontSize: isMobile ? '10px' : '13px'
                        }}>
                          Explore mathematical patterns and relationships
                        </Text>
                      </div>
                    </div>
                  </Card>
                </div>
              ))}
            </Space>

            {/* Expand Arrow */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: isMobile ? '20px' : '32px'
            }}>
              <Button
                type="text"
                style={{
                  border: 'none',
                  background: 'white',
                  borderRadius: '50%',
                  width: isMobile ? '32px' : '40px',
                  height: isMobile ? '32px' : '40px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <span style={{ 
                  fontSize: isMobile ? '14px' : '18px', 
                  transform: 'rotate(90deg)',
                  color: '#8c8c8c'
                }}>
                  ›
                </span>
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default CourseLearning;
